import { PRIMARY_ROLE_OPTIONS, RoleId } from '@common/constants';
import type { UserWithLawFirm } from '@common/types/users';
import ClearIcon from '@mui/icons-material/Clear';
import {
    Box,
    Button,
    FormControl,
    FormHelperText,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { isAxiosError } from 'axios';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '@/services/axiosInstance';
import { updateUser } from '@/services/UserService';
import { errorSnackbar } from '@/utils/snackbar';

interface Props {
    createdUser: UserWithLawFirm;
}

export default function LawfirmCreation({ createdUser }: Props) {
    const navigate = useNavigate();

    const [selectedRole, setSelectedRole] = useState<number | null>(null);
    const [selectedCompany, setSelectedCompany] = useState('');
    const [userRoleError, setUserRoleError] = useState('');
    const [lawFirmNameError, setLawFirmNameError] = useState('');

    const handleRoleChange = async (event: SelectChangeEvent) => {
        setSelectedRole(Number(event.target.value));
        setUserRoleError('');
    };

    const handleStateChange = (event) => {
        setSelectedCompany(event.target.value);
        setLawFirmNameError('');
    };
    const handleClear = () => {
        setSelectedCompany('');
        setLawFirmNameError('');
    };
    const handleContinue = async () => {
        if (selectedRole === null) {
            setUserRoleError('User role is required');
        }
        if (selectedCompany === '') {
            setLawFirmNameError('Company name is required');
        }
        if (userRoleError || lawFirmNameError) return;

        if (!userRoleError && !lawFirmNameError) {
            try {
                const response = await axiosInstance.post('/api/v1/lawfirms', {
                    companyName: selectedCompany,
                });
                if (!selectedRole) return;

                await updateUser(createdUser.userId, {
                    ...createdUser,
                    lawFirmId: response.data.lawFirmId,
                    roleIds: [selectedRole, RoleId.ORGANIZATIONAL_ADMIN],
                });
                navigate('/');
            } catch (err) {
                console.error(err);
                errorSnackbar(
                    isAxiosError(err)
                        ? err.message
                        : 'Failed to create law firm. Please try again.',
                );
            }
        }
    };

    return (
        <Box sx={{ width: '100%' }}>
            <Typography variant="h1">Company Setup</Typography>
            <Typography variant={'body3'} component={'p'} sx={{ marginBottom: '16px' }}>
                Please fill in the details below to setup your company.
            </Typography>
            <Stack spacing={3}>
                <Stack direction="row">
                    <TextField
                        value={selectedCompany}
                        onChange={(event) => handleStateChange(event)}
                        fullWidth
                        placeholder="e.g. Pearson Hardman"
                        type="text"
                        variant="outlined"
                        label="Company Name"
                        error={!!lawFirmNameError}
                        helperText={lawFirmNameError}
                    />
                    {selectedCompany && (
                        <IconButton
                            sx={{ position: 'absolute', top: '0px', right: '4px' }}
                            onClick={handleClear}
                            aria-label="clear"
                        >
                            <ClearIcon />
                        </IconButton>
                    )}
                </Stack>
                <FormControl fullWidth>
                    <InputLabel id="role-id">Select Role</InputLabel>
                    <Select
                        labelId="role-id"
                        label="Select Role"
                        value={selectedRole?.toString() ?? ''}
                        onChange={handleRoleChange}
                        error={!!userRoleError}
                    >
                        {PRIMARY_ROLE_OPTIONS.map((role) => (
                            <MenuItem key={role.roleId} value={role.roleId}>
                                {role.name}
                            </MenuItem>
                        ))}
                    </Select>
                    {userRoleError && <FormHelperText error>{userRoleError}</FormHelperText>}
                </FormControl>
                <Button
                    sx={{ marginTop: '16px' }}
                    variant={'contained'}
                    onClick={handleContinue}
                    disabled={!!lawFirmNameError}
                >
                    Continue
                </Button>
            </Stack>
        </Box>
    );
}
